import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatMoney, formatDate } from '@/lib/api';
import { INVOICE_COLUMNS, DEFAULT_LAYOUT, STATUS_STYLES } from '@/lib/invoiceColumns';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Upload, MagnifyingGlass, ArrowClockwise, CheckCircle, WarningCircle, Sliders, Eye, EyeSlash, ArrowUp, ArrowDown, PushPin, CaretUpDown, CaretUp, CaretDown, ClockCounterClockwise, Trash, Warning, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ExcelFilter, { isFilterActive } from '@/components/ExcelFilter';

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || { label: status || 'غير محدد', dot: '#94a3b8', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${s.bg} ${s.text} ${s.border}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
      {s.label}
    </span>
  );
};

export default function Invoices() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [branchFilter, setBranchFilter] = useState(searchParams.get('branch') || 'all');
  const [followupFilter, setFollowupFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [branches, setBranches] = useState([]);
  const [followupStatuses, setFollowupStatuses] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [colFilters, setColFilters] = useState({}); // { colKey: value }
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ action: '', assigned_user_name: '', follow_up_status: '', classification: '', collectibility: '' });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_count: 0, total_amount: 0, collected_amount: 0, remaining_amount: 0 });
  const fileRef = useRef();

  // Build the query-string params used by BOTH /invoices and /invoices/summary
  const buildServerParams = () => {
    const p = {};
    if (statusFilter !== 'all')  p.status = statusFilter;
    if (branchFilter !== 'all')  p.branch = branchFilter;
    if (followupFilter !== 'all')p.follow_up_status = followupFilter;
    if (classFilter !== 'all')   p.classification = classFilter;
    if (q)                        p.q = q;
    // Server-side per-column filters — Mongo evaluates against the entire DB (not just this page)
    const activeCols = Object.entries(colFilters).filter(([_, f]) => isFilterActive(f));
    if (activeCols.length > 0) {
      p.column_filters = JSON.stringify(Object.fromEntries(activeCols));
    }
    return p;
  };

  const loadSummary = () => {
    api.get('/invoices/summary', { params: buildServerParams() })
      .then(({ data }) => setSummary(data))
      .catch(() => {});
  };

  const load = (nextPage = page) => {
    setLoading(true);
    const params = { ...buildServerParams(), page: nextPage, page_size: pageSize };
    if (sortCol) { params.sort_by = sortCol; params.sort_dir = sortDir || 'desc'; }
    api.get('/invoices', { params })
      .then(({ data }) => {
        // Backend now returns {items,total,page,page_size} — legacy list fallback supported
        if (Array.isArray(data)) { setItems(data); setTotal(data.length); }
        else { setItems(data.items || []); setTotal(data.total || 0); setPage(data.page || 1); }
      })
      .finally(() => setLoading(false));
  };

  // Debounced re-fetch when column filters change so we don't hammer the server on every click
  const colFilterDebounceRef = useRef(null);
  useEffect(() => {
    if (colFilterDebounceRef.current) clearTimeout(colFilterDebounceRef.current);
    colFilterDebounceRef.current = setTimeout(() => { load(1); loadSummary(); setPage(1); }, 250);
    return () => { if (colFilterDebounceRef.current) clearTimeout(colFilterDebounceRef.current); };
    // eslint-disable-next-line
  }, [colFilters]);

  // Re-fetch when server-side filters, sort, page, or pageSize change
  useEffect(() => { load(1); loadSummary(); setPage(1); /* eslint-disable-next-line */ }, [statusFilter, branchFilter, followupFilter, classFilter, q, sortCol, sortDir, pageSize]);

  useEffect(() => {
    load();
    api.get('/meta/branches').then(({ data }) => setBranches(data));
    api.get('/follow-up-statuses').then(({ data }) => setFollowupStatuses(data));
    api.get('/invoice-classifications').then(({ data }) => setClassifications(data));
    api.get('/user-preferences/invoice-columns').then(({ data }) => {
      if (data.layout) {
        const { col_filters: cf, ...layoutOnly } = data.layout;
        setLayout({ ...DEFAULT_LAYOUT, ...layoutOnly });
        if (cf && typeof cf === 'object') setColFilters(cf);
      }
    });
    api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const saveLayout = (l) => {
    setLayout(l);
    api.put('/user-preferences/invoice-columns', { ...l, col_filters: colFilters }).catch(() => {});
  };

  // Persist column filters (Excel-like) — debounced
  const filterSaveRef = useRef(null);
  const updateColFilter = (colKey, filter) => {
    setColFilters(prev => {
      const next = { ...prev };
      if (filter == null) delete next[colKey];
      else next[colKey] = filter;
      // Debounced persist to user preferences
      if (filterSaveRef.current) clearTimeout(filterSaveRef.current);
      filterSaveRef.current = setTimeout(() => {
        api.put('/user-preferences/invoice-columns', { ...layout, col_filters: next }).catch(() => {});
      }, 500);
      return next;
    });
  };

  // Merged order for column manager — includes NEW columns from code even if not in saved layout
  const mergedOrder = useMemo(() => {
    const codeKeys = INVOICE_COLUMNS.map(c => c.key);
    const saved = (layout.order || []).filter(k => codeKeys.includes(k));
    const missing = codeKeys.filter(k => !saved.includes(k));
    return [...saved, ...missing];
  }, [layout.order]);

  const cols = useMemo(() => {
    const byKey = Object.fromEntries(INVOICE_COLUMNS.map(c => [c.key, c]));
    return mergedOrder
      .map(k => byKey[k])
      .filter(c => c && !layout.hidden.includes(c.key))
      .map(c => ({ ...c, label: layout.labels?.[c.key] || c.label }));
  }, [mergedOrder, layout.hidden, layout.labels]);

  // Client-side filtered list = the current page rows (server already applied ALL filters).
  // Sorting is also server-side (via sort_by/sort_dir params), so we return `items` as-is.
  const filtered = items;

  // Distinct values per visible column — fetched from the ENTIRE collection (not just this page).
  // Cached per-column and refreshed whenever server-side filters change so the dropdown always
  // reflects the full DB slice matching the current filter context.
  const [colValues, setColValues] = useState({});
  const distinctFetchRef = useRef({});
  const fetchColValues = useCallback(async (colKey) => {
    // Skip custom.* fetch race for now; the DB endpoint accepts custom.<key> too
    const params = { ...buildServerParams() };
    // Remove this column's OWN filter so dropdown always shows all available options for it
    if (params.column_filters) {
      try {
        const cf = JSON.parse(params.column_filters);
        delete cf[colKey];
        if (Object.keys(cf).length) params.column_filters = JSON.stringify(cf);
        else delete params.column_filters;
      } catch { /* ignore */ }
    }
    const cacheKey = colKey + '|' + JSON.stringify(params);
    if (distinctFetchRef.current[colKey] === cacheKey) return; // already fetched with same context
    distinctFetchRef.current[colKey] = cacheKey;
    try {
      const { data } = await api.get(`/invoices/distinct/${encodeURIComponent(colKey)}`, { params });
      const arr = data.values || [];
      if (data.has_empty) arr.push(null);   // ExcelFilter treats null/'' as (فارغ)
      setColValues(prev => ({ ...prev, [colKey]: arr }));
    } catch { /* ignore individual failures */ }
    // eslint-disable-next-line
  }, [statusFilter, branchFilter, followupFilter, classFilter, q, colFilters]);

  // Invalidate cache when server-side filters change so dropdowns refresh next open
  useEffect(() => { distinctFetchRef.current = {}; }, [statusFilter, branchFilter, followupFilter, classFilter, q, colFilters]);

  const labelFor = (colKey) => (raw) => {
    if (raw == null || raw === '') return '(فارغ)';
    if (colKey === 'follow_up_status') {
      const l = (followupStatuses.find(s => s.code === raw) || {}).label;
      return l || String(raw);
    }
    if (colKey === 'classification') {
      const l = (classifications.find(c => c.code === raw) || {}).label;
      return l || String(raw);
    }
    if (colKey === 'payment_status') return String(raw); // English labels
    return String(raw);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/invoices/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportReport(data);
      toast.success(`جديدة ${data.new} · محدّثة ${data.updated} · بدون تغيير ${data.unchanged}`);
      load();
    } catch (err) {
      toast.error('فشل الاستيراد: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    const { data } = await api.get('/import-history');
    setHistoryList(data);
  };

  const updateFollowup = async (invId, val) => {
    try { await api.put(`/invoices/${invId}`, { follow_up_status: val }); toast.success('تم التحديث'); load(); }
    catch { toast.error('فشل'); }
  };

  const updateClass = async (invId, val) => {
    try { await api.put(`/invoices/${invId}`, { classification: val || null }); toast.success('تم التحديث'); load(); }
    catch { toast.error('فشل'); }
  };

  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
  };

  const bulkApply = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      if (bulkForm.action === 'assign') {
        if (!bulkForm.assigned_user_name) { toast.error('اختر الموظف'); return; }
        await api.post('/invoices/bulk-update', { invoice_ids: ids, updates: { assigned_user_name: bulkForm.assigned_user_name, assigned_user_id: users.find(u => u.name === bulkForm.assigned_user_name)?.id } });
      } else if (bulkForm.action === 'collectibility') {
        await api.post('/invoices/bulk-update', { invoice_ids: ids, updates: { collectibility: bulkForm.collectibility } });
      } else if (bulkForm.action === 'followup') {
        await api.post('/invoices/bulk-update', { invoice_ids: ids, updates: { follow_up_status: bulkForm.follow_up_status } });
      } else if (bulkForm.action === 'classification') {
        await api.post('/invoices/bulk-update', { invoice_ids: ids, updates: { classification: bulkForm.classification } });
      } else if (bulkForm.action === 'whatsapp') {
        // Open wa.me for each invoice's phone in new tabs
        const chosen = items.filter(i => ids.includes(i.id));
        const withPhone = chosen.filter(i => i.phone);
        if (withPhone.length === 0) { toast.error('لا يوجد جوال في المحدد'); return; }
        withPhone.forEach(i => {
          const phone = String(i.phone).replace(/\D/g, '');
          const msg = `عزيزنا ${i.tenant_name || ''}، نود تذكيركم بالفاتورة ${i.payment_no} — المتبقي ${formatMoney(i.remaining_amount)} ريال.`;
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
        });
        toast.success(`فُتحت ${withPhone.length} محادثة واتساب`);
        setBulkOpen(false);
        return;
      } else if (bulkForm.action === 'export') {
        // Professional Excel export of the selected invoices
        try {
          const resp = await api.post('/invoices/export-excel',
            { invoice_ids: ids, columns: cols.map(c => c.key) },
            { responseType: 'blob' });
          const url = URL.createObjectURL(resp.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `invoices-selected-${new Date().toISOString().slice(0,10)}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
          toast.success(`تم تصدير ${ids.length} فاتورة إلى Excel`);
        } catch (e) {
          toast.error('فشل تصدير Excel');
        }
        return;
      } else if (bulkForm.action === 'export_all') {
        try {
          const resp = await api.post('/invoices/export-excel',
            { invoice_ids: filtered.map(i => i.id), columns: cols.map(c => c.key) },
            { responseType: 'blob' });
          const url = URL.createObjectURL(resp.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `invoices-export-${new Date().toISOString().slice(0,10)}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
          toast.success(`تم تصدير ${filtered.length} فاتورة إلى Excel`);
        } catch (e) {
          toast.error('فشل تصدير Excel');
        }
        return;
        const chosen = items.filter(i => ids.includes(i.id));
        const headers = ['رقم الدفعة', 'المستأجر', 'العقار', 'الفرع', 'المبلغ', 'المتبقي', 'الحالة'];
        const rows = chosen.map(i => [i.payment_no, i.tenant_name, i.property_name, i.branch, i.invoice_amount, i.remaining_amount, i.payment_status]);
        const csv = '\uFEFF' + [headers, ...rows].map(r => r.map(x => `"${(x || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `invoices-selected-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`تم تصدير ${chosen.length} فاتورة`);
        setBulkOpen(false);
        return;
      } else {
        return;
      }
      toast.success(`تم تحديث ${ids.length} فاتورة`);
      setBulkOpen(false);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error('فشل: ' + (err.response?.data?.detail || err.message));
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { setConfirmDeleteOpen(false); return; }
    setDeleting(true);
    try {
      const { data } = await api.post('/invoices/bulk-delete', { invoice_ids: ids });
      const c = data.cascade_totals || {};
      const extras = [];
      if (c.payments) extras.push(`${c.payments} سداد`);
      if (c.timeline) extras.push(`${c.timeline} إجراء`);
      if (c.attachments) extras.push(`${c.attachments} مرفق`);
      if (c.legal_claims) extras.push(`${c.legal_claims} مطالبة قانونية`);
      const extraText = extras.length ? ` (مع ${extras.join('، ')})` : '';
      toast.success(`تم حذف ${data.deleted} من ${data.requested} فاتورة${extraText}`);
      setSelected(new Set());
      setConfirmDeleteOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحذف');
    } finally { setDeleting(false); }
  };

  const renderCell = (inv, col) => {
    const v = inv[col.key];
    switch (col.type) {
      case 'money': return <span className="tabular-nums font-medium">{formatMoney(v)}</span>;
      case 'date': return <span className="mono text-slate-600">{formatDate(v)}</span>;
      case 'datetime': return v ? <span className="mono text-slate-600 text-xs">{new Date(v).toLocaleString('en-CA', { hour12: false }).replace(',', '')}</span> : <span className="text-slate-400">—</span>;
      case 'status': return <StatusPill status={v} />;
      case 'followup': {
        const st = followupStatuses.find(s => s.code === v);
        return (
          <select
            value={v || 'new'}
            onChange={(e) => { e.stopPropagation(); updateFollowup(inv.id, e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            className="text-xs rounded-md border border-slate-200 px-2 py-1 bg-white max-w-full"
            style={{ borderColor: st?.color, color: st?.color }}
          >
            {followupStatuses.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        );
      }
      case 'classification': {
        return (
          <select
            value={v || ''}
            onChange={(e) => { e.stopPropagation(); updateClass(inv.id, e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            className="text-xs rounded-md border border-slate-200 px-2 py-1 bg-white max-w-full"
          >
            <option value="">—</option>
            {classifications.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        );
      }
      default: return v || '—';
    }
  };

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Yardi Invoices · Excel-Like</div>
          <h1 className="text-3xl font-bold text-slate-900">الفواتير</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length.toLocaleString()} في هذه الصفحة · إجمالي المطابق: <strong className="text-slate-900">{total.toLocaleString()}</strong> فاتورة</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={openHistory} size="sm"><ClockCounterClockwise className="ms-1" size={16} /> سجل الاستيراد</Button>
          <Button variant="outline" onClick={load} size="sm"><ArrowClockwise className="ms-1" size={16} /> تحديث</Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const resp = await api.post('/invoices/export-excel',
                  { invoice_ids: filtered.map(i => i.id), columns: cols.map(c => c.key) },
                  { responseType: 'blob' });
                const url = URL.createObjectURL(resp.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `invoices-${new Date().toISOString().slice(0,10)}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(`تم تصدير ${filtered.length} فاتورة إلى Excel`);
              } catch { toast.error('فشل التصدير'); }
            }}
            data-testid="export-excel-btn"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white"
          >
            <FileXls className="ms-1" size={16} /> تصدير Excel
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" data-testid="columns-button"><Sliders className="ms-1" size={16} /> الأعمدة</Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-96 overflow-y-auto">
              <SheetHeader><SheetTitle>إدارة الأعمدة</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-slate-500">تثبيت أول</div>
                  <Input type="number" min="0" max={cols.length} value={layout.frozen_count} onChange={(e) => saveLayout({ ...layout, frozen_count: Math.max(0, Math.min(cols.length, Number(e.target.value) || 0)) })} className="w-20 h-8" />
                </div>
                <Button size="sm" variant="ghost" onClick={() => saveLayout(DEFAULT_LAYOUT)} className="w-full">إعادة إلى الافتراضي</Button>
                <div className="border-t border-slate-200 my-2" />
                {mergedOrder.map((k, idx) => {
                  const col = INVOICE_COLUMNS.find(c => c.key === k);
                  if (!col) return null;
                  const hidden = layout.hidden.includes(k);
                  const label = layout.labels?.[k] || col.label;
                  return (
                    <div key={k} className="flex items-center gap-1 p-2 border border-slate-200 rounded-md" data-testid={`col-manage-${k}`}>
                      <button
                        onClick={() => {
                          const next = layout.hidden.includes(k) ? layout.hidden.filter(h => h !== k) : [...layout.hidden, k];
                          saveLayout({ ...layout, hidden: next });
                        }}
                        className="text-slate-500 hover:text-slate-900"
                        title={hidden ? 'إظهار' : 'إخفاء'}
                      >
                        {hidden ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                      <Input
                        value={label}
                        onChange={(e) => saveLayout({ ...layout, labels: { ...(layout.labels || {}), [k]: e.target.value } })}
                        className="h-8 text-sm flex-1"
                      />
                      <button
                        onClick={() => {
                          if (idx === 0) return;
                          const next = [...mergedOrder];
                          [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                          saveLayout({ ...layout, order: next });
                        }}
                        className="text-slate-500 hover:text-slate-900"
                      ><ArrowUp size={14} /></button>
                      <button
                        onClick={() => {
                          if (idx === mergedOrder.length - 1) return;
                          const next = [...mergedOrder];
                          [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                          saveLayout({ ...layout, order: next });
                        }}
                        className="text-slate-500 hover:text-slate-900"
                      ><ArrowDown size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" size="sm" data-testid="import-invoices-button">
                <Upload className="ms-1" size={16} /> استيراد Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>استيراد فواتير Yardi</DialogTitle>
                <DialogDescription>سيتم مطابقة الفواتير برقم الدفعة، وتحديث بيانات Yardi مع الاحتفاظ بكل ملاحظات وإجراءات المتابعة.</DialogDescription>
              </DialogHeader>
              <label className="border-2 border-dashed border-slate-300 rounded-md p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[#B4935A]">
                <Upload size={36} className="text-slate-400 mb-3" />
                <div className="text-sm font-medium">اضغط لرفع ملف Excel</div>
                <div className="text-xs text-slate-500 mt-1">.xlsx / .xls</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" disabled={importing} data-testid="import-file-input" />
              </label>
              {importing && <div className="text-sm text-center">جاري المعالجة...</div>}
              {importReport && (
                <div className="border border-slate-200 rounded-md p-4 space-y-3 bg-slate-50">
                  <div className="font-semibold flex items-center gap-2"><CheckCircle className="text-emerald-600" /> تقرير الاستيراد</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Stat label="إجمالي" value={importReport.total_rows} />
                    <Stat label="جديدة" value={importReport.new} tone="text-emerald-700" />
                    <Stat label="محدّثة" value={importReport.updated} tone="text-blue-700" />
                    <Stat label="بدون تغيير" value={importReport.unchanged} tone="text-slate-500" />
                    <Stat label="مغلقة" value={importReport.closed} tone="text-emerald-600" />
                    <Stat label="جزئية" value={importReport.partial} tone="text-amber-700" />
                    <Stat label="غير مسددة" value={importReport.unpaid} tone="text-red-700" />
                    <Stat label="تغيرت الحالة" value={importReport.status_changed} tone="text-amber-600" />
                  </div>
                  {importReport.error_count > 0 && (
                    <div className="text-xs text-red-600 flex items-center gap-1"><WarningCircle /> {importReport.error_count} صف به خطأ</div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary strip — reflects ALL invoices matching current server-side filters (not the paged view). Clickable for drill-down. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat
          label="إجمالي عدد الفواتير"
          value={Number(summary.total_count || 0).toLocaleString('en-US')}
          hint="اضغط لعرض جميع الفواتير"
          onClick={() => {
            setStatusFilter('all'); setBranchFilter('all');
            setFollowupFilter('all'); setClassFilter('all');
            setQ(''); setColFilters({});
            toast.success('تمّ عرض جميع الفواتير');
          }}
          testId="stat-total-count"
        />
        <MiniStat
          label="إجمالي قيمة الفواتير"
          value={formatMoney(summary.total_amount)}
          hint="اضغط لعرض جميع الفواتير"
          onClick={() => {
            setStatusFilter('all'); setBranchFilter('all');
            setFollowupFilter('all'); setClassFilter('all');
            setQ(''); setColFilters({});
            toast.success('تمّ عرض جميع الفواتير');
          }}
          testId="stat-total-amount"
        />
        <MiniStat
          label="إجمالي المبلغ المحصل"
          value={formatMoney(summary.collected_amount)}
          tone="text-emerald-600"
          hint="اضغط لعرض الفواتير المسدّدة بالكامل"
          onClick={() => {
            setStatusFilter('Fully Paid');
            toast.success('تمّت الفلترة على الفواتير المسدّدة بالكامل');
          }}
          testId="stat-collected"
        />
        <MiniStat
          label="إجمالي المبلغ المتبقي"
          value={formatMoney(summary.remaining_amount)}
          tone="text-red-600"
          hint="اضغط لعرض الفواتير غير المسدّدة"
          onClick={() => {
            setStatusFilter('Unpaid');
            toast.success('تمّت الفلترة على الفواتير غير المسدّدة');
          }}
          testId="stat-remaining"
        />
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <Card className="p-3 border border-[#B4935A] bg-[#B4935A]/5 rounded-md shadow-none flex flex-wrap items-center gap-2" data-testid="bulk-bar">
          <div className="text-sm font-medium">
            <span className="tabular-nums text-[#B4935A] font-bold">{selected.size}</span> فاتورة محددة
          </div>
          <div className="flex-1 min-w-4" />
          <Button size="sm" variant="outline" onClick={() => { setBulkForm({ action: 'assign' }); setBulkOpen(true); }} data-testid="bulk-assign">تعيين موظف</Button>
          <Button size="sm" variant="outline" onClick={() => { setBulkForm({ action: 'followup' }); setBulkOpen(true); }} data-testid="bulk-followup">حالة المتابعة</Button>
          <Button size="sm" variant="outline" onClick={() => { setBulkForm({ action: 'classification' }); setBulkOpen(true); }} data-testid="bulk-class">التصنيف</Button>
          <Button size="sm" variant="outline" onClick={() => { setBulkForm({ action: 'collectibility' }); setBulkOpen(true); }} data-testid="bulk-collect">قابلية التحصيل</Button>
          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => { setBulkForm({ action: 'whatsapp' }); bulkApply(); }} data-testid="bulk-whatsapp">إرسال واتساب</Button>
          <Button size="sm" variant="outline" onClick={() => { setBulkForm({ action: 'export' }); bulkApply(); }} data-testid="bulk-export">تصدير CSV</Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setConfirmDeleteOpen(true)} data-testid="bulk-delete">
            <Trash className="ms-1" size={14} /> حذف الفواتير
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>إلغاء</Button>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-3 border border-slate-200 rounded-md shadow-none">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث عام..." className="pr-9 h-9" data-testid="invoices-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payment Statuses</SelectItem>
              <SelectItem value="Unpaid">
                <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" /> Unpaid</span>
              </SelectItem>
              <SelectItem value="Partially Paid">
                <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" /> Partially Paid</span>
              </SelectItem>
              <SelectItem value="Fully Paid">
                <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Fully Paid</span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={followupFilter} onValueChange={setFollowupFilter}>
            <SelectTrigger className="w-44 h-9"><SelectValue placeholder="حالة المتابعة" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل حالات المتابعة</SelectItem>
              {followupStatuses.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-44 h-9"><SelectValue placeholder="التصنيف" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل التصنيفات</SelectItem>
              {classifications.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الفروع</SelectItem>
              {branches.map(b => <SelectItem key={b.code} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {(sortCol || Object.values(colFilters).some(isFilterActive)) && (
            <Button size="sm" variant="ghost" onClick={() => { setSortCol(null); setColFilters({}); api.put('/user-preferences/invoice-columns', { ...layout, col_filters: {} }).catch(() => {}); }} data-testid="clear-filters-btn">مسح الفرز والفلاتر</Button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-380px)]">
          <table className="text-sm data-table" style={{ minWidth: cols.reduce((s, c) => s + (c.width || 120), 60) }}>
            <thead className="bg-slate-50 sticky top-0 z-20">
              <tr>
                <th className="p-2 w-10 text-center bg-slate-50 sticky top-0" style={{ position: 'sticky', insetInlineEnd: 0, zIndex: 25 }}>
                  <Checkbox
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onCheckedChange={(v) => setSelected(v ? new Set(filtered.map(i => i.id)) : new Set())}
                  />
                </th>
                {cols.map((c, idx) => {
                  const isFrozen = idx < layout.frozen_count;
                  return (
                    <th
                      key={c.key}
                      className={`p-2 text-start font-medium text-xs uppercase tracking-wider text-slate-700 bg-slate-50 whitespace-nowrap ${isFrozen ? 'shadow-[inset_-1px_0_0_#e2e8f0]' : ''}`}
                      style={{ width: c.width, minWidth: c.width, ...(isFrozen ? { position: 'sticky', insetInlineEnd: 40 + cols.slice(0, idx).reduce((s, x) => s + (x.width || 120), 0), zIndex: 15 } : {}) }}
                    >
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <button className="flex items-center gap-1 hover:text-slate-900" onClick={() => toggleSort(c.key)}>
                          {c.label}
                          {sortCol === c.key ? (sortDir === 'asc' ? <CaretUp size={12} /> : <CaretDown size={12} />) : <CaretUpDown size={12} className="opacity-40" />}
                        </button>
                        <ExcelFilter
                          column={c}
                          values={colValues[c.key] || []}
                          onOpen={() => fetchColValues(c.key)}
                          filter={colFilters[c.key]}
                          onChange={(f) => updateColFilter(c.key, f)}
                          sort={sortCol === c.key ? sortDir : null}
                          onSort={(dir) => {
                            if (dir === null) { setSortCol(null); }
                            else { setSortCol(c.key); setSortDir(dir); }
                          }}
                          labelFor={labelFor(c.key)}
                        />
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={cols.length + 1} className="p-6 text-center text-slate-500">جاري التحميل...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={cols.length + 1} className="p-8 text-center text-slate-500">لا توجد فواتير مطابقة</td></tr>
              ) : filtered.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" style={{ contentVisibility: 'auto', containIntrinsicSize: '48px' }} data-testid={`invoice-row-${inv.payment_no}`}>
                  <td className="p-2 text-center bg-white sticky" style={{ position: 'sticky', insetInlineEnd: 0, zIndex: 5 }} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(inv.id)}
                      onCheckedChange={(v) => {
                        const next = new Set(selected);
                        if (v) next.add(inv.id); else next.delete(inv.id);
                        setSelected(next);
                      }}
                    />
                  </td>
                  {cols.map((c, idx) => {
                    const isFrozen = idx < layout.frozen_count;
                    return (
                      <td
                        key={c.key}
                        onClick={() => nav(`/invoices/${inv.id}`)}
                        className={`p-2 whitespace-nowrap bg-white ${isFrozen ? 'shadow-[inset_-1px_0_0_#e2e8f0]' : ''}`}
                        style={{ width: c.width, minWidth: c.width, ...(isFrozen ? { position: 'sticky', insetInlineEnd: 40 + cols.slice(0, idx).reduce((s, x) => s + (x.width || 120), 0), zIndex: 3 } : {}) }}
                      >
                        {renderCell(inv, c)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > pageSize && (
          <div className="border-t border-slate-200 p-3 flex items-center justify-between gap-4 flex-wrap text-sm" data-testid="invoices-pager">
            <div className="text-slate-600">
              الصفحة {page} من {Math.max(1, Math.ceil(total / pageSize))} · إجمالي {total.toLocaleString()} فاتورة
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">حجم الصفحة:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v)); }}>
                <SelectTrigger className="h-8 w-20" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p); }} data-testid="prev-page-btn">السابق</Button>
              <Button size="sm" variant="outline" disabled={page * pageSize >= total} onClick={() => { const p = page + 1; setPage(p); load(p); }} data-testid="next-page-btn">التالي</Button>
            </div>
          </div>
        )}
        {filtered.length > pageSize && (
          <div className="p-2 text-center text-xs text-slate-500 border-t">
            يعرض أول 800 من أصل {filtered.length}. استخدم الفلاتر للتضييق.
          </div>
        )}
      </Card>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>إجراء جماعي على {selected.size} فاتورة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {bulkForm.action === 'assign' && (
              <div>
                <label className="text-sm text-slate-700 mb-1 block">اختر الموظف المسؤول</label>
                <select
                  value={bulkForm.assigned_user_name || ''}
                  onChange={(e) => setBulkForm({ ...bulkForm, assigned_user_name: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                  data-testid="bulk-assign-select"
                >
                  <option value="">—</option>
                  {users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.role})</option>)}
                </select>
              </div>
            )}
            {bulkForm.action === 'followup' && (
              <div>
                <label className="text-sm text-slate-700 mb-1 block">حالة المتابعة الجديدة</label>
                <select
                  value={bulkForm.follow_up_status || ''}
                  onChange={(e) => setBulkForm({ ...bulkForm, follow_up_status: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                  data-testid="bulk-followup-select"
                >
                  <option value="">—</option>
                  {followupStatuses.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                </select>
              </div>
            )}
            {bulkForm.action === 'classification' && (
              <div>
                <label className="text-sm text-slate-700 mb-1 block">التصنيف الجديد</label>
                <select
                  value={bulkForm.classification || ''}
                  onChange={(e) => setBulkForm({ ...bulkForm, classification: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                >
                  <option value="">—</option>
                  {classifications.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            )}
            {bulkForm.action === 'collectibility' && (
              <div>
                <label className="text-sm text-slate-700 mb-1 block">قابلية التحصيل</label>
                <select
                  value={bulkForm.collectibility || ''}
                  onChange={(e) => setBulkForm({ ...bulkForm, collectibility: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                >
                  <option value="">—</option>
                  <option value="high">قابلة للتحصيل</option>
                  <option value="medium">متوسطة</option>
                  <option value="low">صعبة</option>
                  <option value="uncollectible">غير قابلة للتحصيل</option>
                </select>
              </div>
            )}
            <Button onClick={bulkApply} className="w-full bg-slate-900 text-white" data-testid="bulk-apply-button">تطبيق</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm bulk-delete Dialog */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Warning size={22} weight="fill" /> تأكيد حذف الفواتير
            </DialogTitle>
            <DialogDescription>
              سيتم حذف <span className="font-bold text-slate-900">{selected.size}</span> فاتورة نهائياً مع جميع بياناتها المرتبطة.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm space-y-1.5">
            <div className="font-semibold text-red-700 mb-1">سيتم حذف تلقائياً:</div>
            <ul className="list-disc pr-5 text-slate-700 leading-loose">
              <li>جميع السدادات اليدوية المسجّلة على هذه الفواتير</li>
              <li>جميع إجراءات المتابعة والـ Timeline المرتبطة</li>
              <li>جميع المرفقات على هذه الفواتير</li>
              <li>جميع المطالبات القانونية المُحالة من هذه الفواتير (والـ Timeline التابع لها)</li>
              <li>المهام المرتبطة</li>
            </ul>
            <div className="text-xs text-red-800 mt-2 font-medium">⚠️ هذا الإجراء لا يمكن التراجع عنه.</div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>إلغاء</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={bulkDelete} disabled={deleting} data-testid="confirm-bulk-delete-btn">
              <Trash className="ms-1" size={14} /> {deleting ? 'جارٍ الحذف...' : `حذف ${selected.size} فاتورة`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>سجل الاستيراد</DialogTitle></DialogHeader>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-start p-2 text-xs">التاريخ</th>
                <th className="text-start p-2 text-xs">الملف</th>
                <th className="text-start p-2 text-xs">المستخدم</th>
                <th className="text-end p-2 text-xs">إجمالي</th>
                <th className="text-end p-2 text-xs">جديدة</th>
                <th className="text-end p-2 text-xs">محدّثة</th>
                <th className="text-end p-2 text-xs">تغيرت</th>
              </tr>
            </thead>
            <tbody>
              {historyList.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-slate-500">لا يوجد سجل</td></tr> :
                historyList.map(h => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="p-2 mono text-xs">{new Date(h.created_at).toLocaleString('ar-SA')}</td>
                    <td className="p-2 truncate max-w-[200px]">{h.file_name}</td>
                    <td className="p-2 text-slate-600">{h.imported_by_name}</td>
                    <td className="p-2 text-end tabular-nums">{h.total_rows}</td>
                    <td className="p-2 text-end tabular-nums text-emerald-700">{h.new}</td>
                    <td className="p-2 text-end tabular-nums text-blue-700">{h.updated}</td>
                    <td className="p-2 text-end tabular-nums text-amber-700">{h.status_changed}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MiniStat = ({ label, value, tone = 'text-slate-900', hint, onClick, testId }) => {
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      data-testid={testId}
      className={`text-start bg-white border border-slate-200 rounded-md px-4 py-3 transition
        ${clickable ? 'hover:border-[#B4935A] hover:shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#B4935A]/40' : 'cursor-default'}`}
    >
      <div className="text-xs text-slate-500 flex items-center justify-between gap-2">
        <span>{label}</span>
        {clickable && <span className="text-[10px] text-[#B4935A] opacity-0 group-hover:opacity-100">↩</span>}
      </div>
      <div className={`text-lg font-bold tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </button>
  );
};

const Stat = ({ label, value, tone = 'text-slate-900' }) => (
  <div className="flex justify-between border border-slate-200 rounded-md px-3 py-1.5 bg-white">
    <span className="text-slate-600 text-xs">{label}</span>
    <span className={`font-semibold tabular-nums ${tone}`}>{value ?? 0}</span>
  </div>
);
